import { compareDesc } from 'date-fns'
import { AssetCardProps } from 'features/assetDatabase/types/assetCardTypes'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoPublishingState,
  GeckoUnit,
} from 'types'

export const useAssetCardsData = (
  assetModels: ReadonlyArray<GeckoAssetModel>,
  units: ReadonlyArray<GeckoUnit>,
  components: ReadonlyArray<GeckoComponent>,
  cdfs: ReadonlyArray<GeckoComponentDataFrame>,
  componentDefaults: ReadonlyArray<GeckoComponentDefaults>,
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
) => {
  return useMemo(() => {
    const result = new Array<AssetCardProps>()
    units.forEach((unit) => {
      // Filter and sort CDFs for this unit
      const unitCDFs = cdfs
        .filter((cdf) => cdf.tags.unit === unit.id)
        .sort((a, b) => {
          const dateA = a.data.params.date
          const dateB = b.data.params.date
          if (!dateA && !dateB) return 0
          if (!dateA) return 1 // Prefer a CDF with a date to one without
          if (!dateB) return -1
          return compareDesc(dateA, dateB) // Prefer more recent CDFs
        })

      // Prefer a non-raw CDF using similar eligibility logic as Details page
      const isNonRaw = (cdf: GeckoComponentDataFrame) => {
        const cdsDirect = componentDatasets.find(
          (ds) => ds.id === cdf.tags['component-dataset']
        )
        // If no direct CDS, try to infer via matching inspection/component
        const cdsFallback = componentDatasets.find(
          (ds) =>
            ds.tags.inspection === cdf.tags.inspection &&
            ds.tags.component === cdf.tags.component
        )
        const cds = cdsDirect ?? cdsFallback
        // If we can't determine publishing_state, assume eligible
        if (!cds) return true
        return cds.data.publishing_state.id !== GeckoPublishingState.Raw.id
      }

      const nonRawUnitCDFsSorted = unitCDFs
        .filter((cdf) => isNonRaw(cdf))
        .sort(GeckoComponentDataFrame.GetDefaultSort(componentDatasets))

      const assetModel = assetModels.find(
        (assetModel) => assetModel.tags.unit === unit.id
      )

      const selectedCDF = nonRawUnitCDFsSorted.at(0) ?? unitCDFs.at(0)

      const selectedComponent =
        (selectedCDF
          ? components.find((c) => c.id === selectedCDF.tags.component)
          : components.find((c) => assetModel?.data.components.has(c.id))) ??
        components.find((c) => c.tags.unit === unit.id)

      const selectedComponentDefaults = selectedComponent
        ? componentDefaults.find(
            (defaults) => defaults.tags.component === selectedComponent.id
          )
        : undefined

      // Only show units that have components, unless user has core:edit permissions (in which case, show all)
      if (selectedComponent) {
        result.push({
          unit: unit,
          assetModel: assetModel,
          component: selectedComponent,
          getColorThresholdForComponent: (cdf) =>
            selectedComponentDefaults?.data.color_scale_thresholds.get(
              cdf.data.params.view.id
            ),
          componentDataFrame: selectedCDF,
        })
      }
    })

    return result.sort((a, b) => GeckoUnit.DefaultSort(a.unit, b.unit))
  }, [
    assetModels,
    units,
    components,
    cdfs,
    componentDefaults,
    componentDatasets,
  ])
}
